import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentMessages } from './api/studentScreensApi'

const PAGE_SIZES = [10, 25, 50, 100]

export default function StudentMessagesScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pageSize, setPageSize] = useState(10)
  const [messages, setMessages] = useState([])

  useEffect(() => {
    loadStudent()
    loadMessages()
  }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadMessages = async () => {
    try {
      const rows = await getStudentMessages({ studentId })
      setMessages(rows || [])
    } catch {
      setMessages([])
    }
  }

  const pagedMessages = useMemo(() => messages.slice(0, pageSize), [messages, pageSize])

  const goBack = () => navigate('/admission/students/list')
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Messages</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            Msg Log — {studentName}
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-on-primary text-[22px]">chat_bubble</span>
          <h2 className="text-lg font-bold text-on-primary">Msg log</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-on-primary/80">Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded-lg border border-white/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-white/40"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-xs font-bold text-on-primary/80">entries</span>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
            <p className="mt-3 text-sm text-on-surface-variant">Loading messages...</p>
          </div>
        ) : pagedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <span className="material-symbols-outlined text-4xl text-outline">chat_bubble_outline</span>
            <p className="mt-3 text-sm font-medium text-on-surface-variant">No messages found for this student.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/30 bg-surface-container-low">
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[250px]">Status & Student</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[200px]">Delivery Status</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Message</th>
                <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[200px]">Timestamp & Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {pagedMessages.map((msg, idx) => (
                <tr
                  key={msg.id}
                  className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}
                >
                  <td className="px-5 py-4 align-top">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <span className="material-symbols-outlined text-[14px]">check</span>
                      </span>
                      <div>
                        <p className="text-sm font-bold text-primary">{msg.studentName}</p>
                        <p className="mt-0.5 text-xs text-on-surface-variant font-mono">{msg.mobile}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 align-top">
                    <p className="text-xs font-bold text-green-700">
                      Delivered on {msg.deliveredAt}
                    </p>
                    <p className="mt-1 text-[11px] text-on-surface-variant">
                      Sent by: {msg.sentBy}
                    </p>
                  </td>
                  <td className="px-5 py-4 align-top">
                    <p className="text-sm leading-relaxed text-on-surface">{msg.body}</p>
                  </td>
                  <td className="px-5 py-4 align-top">
                    <p className="text-xs font-semibold text-on-surface">{msg.sentAt}</p>
                    <p className="mt-1">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          msg.routeType === 'Transactional'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {msg.routeType}
                      </span>
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>
              Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.createdDate}</span>
            </span>
          )}
          {student?.updatedBy && (
            <span>
              Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.updatedDate}</span>
            </span>
          )}
        </div>
      </section>
    </div>
  )
}
